import {doNothing} from '@raison/toolbox';
import {AccountId} from '@raison/types';
import {RsnCheckbox, RsnDialog, RsnIconWidget} from '@raison/ui-kit';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {TransactionsHistory} from '~/entities/transactions-history';
import {
    FilterType,
    filterTypes,
    selectAccountFilterResource,
} from '~/processes/transactions/transactions-history/TransactionFiltersModals/useAccountFilterResource';
import {FilterPopupLayout} from '../FilterPopupLayout';
import BottomButtons from './BottomButtons/BottomButtons';
import {isFilterUnchanged} from './shared';

type AccountFilters = Record<FilterType, AccountId[]>;

const nothingSelected: AccountFilters = {
    account: [],
    wallet: [],
    token: [],
};

export const AccountFilterPopup = () => {
    const {data, isSuccess} = useSelector(selectAccountFilterResource);
    const {onClose: close = doNothing} = RsnDialog.useControls();

    const [appliedFilters, {apply: applyFilters}] = TransactionsHistory.useFilters();
    const [selectedFilters, setSelected] = useState<AccountFilters>(() => ({
        account: appliedFilters.account ?? [],
        wallet: appliedFilters.wallet ?? [],
        token: appliedFilters.token ?? [],
    }));

    const nothingChanged = filterTypes.every(type => isFilterUnchanged(appliedFilters[type], selectedFilters[type]));
    const noFiltersSelected = filterTypes.every(type => selectedFilters[type].length === 0);

    const handleCheck = (id: AccountId, name: FilterType) => {
        const list = [...selectedFilters[name]];
        const index = list.findIndex(item => item === id);
        if (index > -1) {
            list.splice(index, 1);
        } else {
            list.push(id);
        }
        setSelected(prevState => ({...prevState, [name]: list}));
    };

    const onApply = () => {
        applyFilters(selectedFilters);
        close();
    };

    const onClear = () => {
        setSelected(nothingSelected);
    };

    return (
        <FilterPopupLayout
            title='Account'
            padding={'none'}
            footer={
                <BottomButtons
                    isApplyDisabled={nothingChanged}
                    isClearHidden={noFiltersSelected}
                    applyCallback={onApply}
                    clearCallback={onClear}
                />
            }
        >
            {isSuccess && (
                <div className={'flex flex-col'}>
                    {Object.entries(data).map(([name, array], index) => (
                        <div key={`name-${index}`}>
                            <div className={'fv-heading-s p-4 pb-2 text-left text-neutral-100'}>{name} account</div>
                            <div>
                                {array?.map(item => {
                                    const name = item.type;
                                    return (
                                        <RsnIconWidget.Lg
                                            key={item.id}
                                            icon={{src: item.icon || ''}}
                                            as='label'
                                            className={'p-4'}
                                            addon={
                                                <RsnCheckbox
                                                    onChange={() => handleCheck(item.id, item.type)}
                                                    checked={selectedFilters[name].includes(item.id)}
                                                />
                                            }
                                        >
                                            <RsnIconWidget.TextBlock
                                                multiline
                                                heading={item.name}
                                                details={item.currency}
                                            />
                                        </RsnIconWidget.Lg>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </FilterPopupLayout>
    );
};
